/* eslint-disable react/no-unknown-property */
import { Button, TextCaption } from '@pypestream/design-system';
import { FC, useMemo, useState } from 'react';

import { PanelHead } from './validate-panels';
import type { RunChannel } from './validate-runs';
import {
  REPEATS,
  WEEKDAYS,
  describeSchedule,
  nextRunAfter,
  type ScheduleRepeat,
  type SuiteSchedule,
} from './validate-schedules';
import type { ValidateSuite } from './validate-suites';

/**
 * Schedule a suite (the testing tool's scheduled runs): once, or on repeat,
 * on the server — its runs land in the shared history, by "Scheduled".
 */

const localZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const today = () => {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export const SchedulePanel: FC<{
  suite: ValidateSuite;
  channel: RunChannel | null;
  existing: SuiteSchedule | null;
  /** The solution's Quality judge now, for the scores to match the page's. */
  judge: unknown;
  /** False where the shared store isn't set up — schedules need it. */
  available: boolean;
  makeId: () => string;
  onSave: (schedule: SuiteSchedule) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
}> = ({
  suite,
  channel,
  existing,
  judge,
  available,
  makeId,
  onSave,
  onDelete,
  onClose,
}) => {
  const [repeat, setRepeat] = useState<ScheduleRepeat>(
    existing?.repeat ?? 'weekdays'
  );
  const [time, setTime] = useState(existing?.time ?? '09:00');
  const [weekday, setWeekday] = useState(existing?.weekday ?? 1);
  const [date, setDate] = useState(existing?.date ?? today());
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeZone = existing?.timeZone ?? localZone();

  const draft = { repeat, time, weekday, date, timeZone, enabled };
  const next = useMemo(
    () => nextRunAfter(draft, Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repeat, time, weekday, date, timeZone, enabled]
  );

  const save = async () => {
    if (!channel) return;
    setBusy(true);
    setError(null);
    const now = Date.now();
    try {
      await onSave({
        id: existing?.id ?? makeId(),
        suiteId: suite.id,
        suiteName: suite.name,
        repeat,
        time,
        ...(repeat === 'weekly' ? { weekday } : {}),
        ...(repeat === 'once' ? { date } : {}),
        timeZone,
        enabled,
        nextRunAt: next,
        lastRunAt: existing?.lastRunAt ?? null,
        lastSuiteRunId: existing?.lastSuiteRunId ?? null,
        channel,
        origin: window.location.origin,
        judge,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="c-validate-panel" test-id="bobcat-validate-schedule-panel">
      <PanelHead
        title="Schedule suite"
        icon="clock"
        sub={`Runs “${suite.name}” on the server, with nobody’s browser open. Its runs show in Runs for everyone, marked Scheduled.`}
        onClose={onClose}
      />
      <div className="c-validate-panel__scroll">
        {!available && (
          <p className="c-validate-error" role="alert">
            Schedules need the shared history, which isn’t set up on this
            deployment yet.
          </p>
        )}
        {!channel && (
          <p className="c-validate-error" role="alert">
            Pick the suite’s channel first: a schedule runs on it.
          </p>
        )}
        <label className="c-validate-field">
          <span>Repeat</span>
          <select
            className="c-validate-select"
            value={repeat}
            test-id="bobcat-validate-schedule-repeat"
            onChange={(e) => setRepeat(e.target.value as ScheduleRepeat)}
          >
            {REPEATS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <div className="c-validate-field-row">
          {repeat === 'once' && (
            <label className="c-validate-field">
              <span>Date</span>
              <input
                type="date"
                value={date}
                test-id="bobcat-validate-schedule-date"
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
          )}
          {repeat === 'weekly' && (
            <label className="c-validate-field">
              <span>Day</span>
              <select
                className="c-validate-select"
                value={weekday}
                test-id="bobcat-validate-schedule-weekday"
                onChange={(e) => setWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="c-validate-field">
            <span>{repeat === 'hourly' ? 'At minute' : 'Time'}</span>
            <input
              type="time"
              value={time}
              test-id="bobcat-validate-schedule-time"
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
        </div>
        <label className="c-validate-field c-validate-field--inline">
          <input
            type="checkbox"
            checked={enabled}
            test-id="bobcat-validate-schedule-enabled"
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>On</span>
        </label>
        <TextCaption>
          {describeSchedule(draft)} ·{' '}
          {next
            ? `next run ${new Date(next).toLocaleString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}`
            : 'no run coming up'}
        </TextCaption>
        {existing?.lastRunAt && (
          <TextCaption>
            Last ran {new Date(existing.lastRunAt).toLocaleString()}
          </TextCaption>
        )}
        {error && (
          <p className="c-validate-error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="c-validate-panel__actions">
        <Button
          size="small"
          variant="primary"
          test-id="bobcat-validate-schedule-save"
          disabled={!available || !channel || busy || (enabled && !next)}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save schedule'}
        </Button>
        {existing && (
          <Button
            size="small"
            variant="secondary"
            test-id="bobcat-validate-schedule-delete"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onDelete(existing.id)
                .then(onClose)
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : String(err))
                )
                .finally(() => setBusy(false));
            }}
          >
            Remove
          </Button>
        )}
        <Button size="small" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
